import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  SectionCard,
  FieldLabel,
  ReadOnlyField,
  RadioGroup,
  BottomActionBar,
  WarningBanner,
  ApprovalTimeline,
  InfoRow,
  useConfirm,
} from "./shared/components";
import {
  getEmployeeDetails,
  getMyResignations,
  checkBondWaiverEligibility,
  submitBondWaiver,
  formatDate,
  formatINR,
  EmployeeDetails,
  BondWaiverRequest,
} from "./services/eSeparation.service";

const BondWaiver: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routes = all_routes;
  const origRequestId = (location.state as any)?.orig_request_id || "";

  const [emp, setEmp] = useState<EmployeeDetails | null>(null);
  const [elig, setElig] = useState<any>({ has_active_resignation: true, has_active_bond: true });
  const [bond, setBond] = useState<any>({});
  const [resig, setResig] = useState<{
    intimation_date: string;
    notice_end_date: string;
    relieved_wef: string;
    request_id: string;
  }>({ intimation_date: "", notice_end_date: "", relieved_wef: "", request_id: "" });
  const [waiverType, setWaiverType] = useState("full");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    getEmployeeDetails().then((res) => res.data && setEmp(res.data));
    checkBondWaiverEligibility().then((res) => {
      setElig(res.data || {});
      setBond(res.data?.bond || {});
    });
    getMyResignations().then((res) => {
      const list = res.data || [];
      const active = list.find(
        (r) => r.status !== "Closed" && r.status !== "REJECTED"
      );
      const raw = active?.raw;
      if (!raw) return;
      // Mobile uses the same intimation_date value for each date field.
      const intimation = raw.intimation_date || "";
      setResig({
        intimation_date: intimation,
        notice_end_date: intimation,
        relieved_wef: intimation,
        request_id: raw.request_id || "",
      });
    });
  }, []);

  const bondAmount = Number(bond?.bond_amount) || 0;
  const noResignation = elig?.has_active_resignation === false;
  const noBond = elig?.has_active_bond === false;

  const handleCancel = async () => {
    if (await confirm("Discard changes?", { title: "Cancel", confirmLabel: "Discard", tone: "danger" }))
      navigate(-1);
  };

  const handleSubmit = async () => {
    if (!reason.trim())
      return toast.warning("Please provide a reason for the waiver request.");
    if (waiverType === "partial" && !requestedAmount.trim())
      return toast.warning("Please enter the requested waiver amount for partial waiver.");
    if (
      !(await confirm(
        "Are you sure you want to submit this bond waiver request? It will be routed to the Managing Director for final approval.",
        { title: "Confirm Submission", confirmLabel: "Submit", tone: "primary" }
      ))
    )
      return;
    const payload: BondWaiverRequest = {
      orig_request_id: origRequestId || resig.request_id,
      emp_no: emp?.emp_no || "",
      waiver_date: today,
      waiver_reason: reason,
      waiver_type: waiverType as "full" | "partial",
      requested_waiver_amount:
        waiverType === "partial" ? Number(requestedAmount) || 0 : bondAmount,
      bond_period: bond?.bond_period,
      bond_amount: bondAmount,
      training_expenditure: Number(bond?.training_expenditure) || 0,
      service_completed_under_bond: bond?.service_completed,
    };
    setSubmitting(true);
    const res = await submitBondWaiver(payload);
    setSubmitting(false);
    if (res.success) {
      toast.success(
        "Your bond waiver request has been submitted and will be reviewed by the approval authorities."
      );
      navigate(routes.Esepration.overview);
    } else {
      toast.error(res.message || "Submission failed.");
    }
  };

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-0">
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body">
            <h3 className="page-title mb-1">Waiver of Service Bond</h3>
            <p className="text-muted mb-0">Request partial or full waiver of bond obligations.</p>
          </div>
        </div>

        {noResignation && (
          <WarningBanner type="error" message="You must have an active resignation request before applying for a bond waiver." />
        )}
        {noBond && (
          <WarningBanner type="error" message="No active service bond found for your employee record. Bond waiver is not applicable." />
        )}
        {!noResignation && !noBond && (
          <WarningBanner type="warning" message="Bond waiver requires approval from the Managing Director (MD) as the final authority." />
        )}

        {/* Section 1 — Employee & Resignation */}
        <SectionCard index={0} title="Employee & Resignation Details">
          <WarningBanner type="info" message="Details auto-fetched from your profile and resignation record." />
          <div className="row g-3">
            <div className="col-md-6"><FieldLabel label="Employee Name" /><ReadOnlyField value={emp?.emp_name} /></div>
            <div className="col-md-6"><FieldLabel label="Employee No." /><ReadOnlyField value={emp?.emp_no} /></div>
            <div className="col-md-6"><FieldLabel label="Designation" /><ReadOnlyField value={emp?.designation} /></div>
            <div className="col-md-6"><FieldLabel label="Department / Posting" /><ReadOnlyField value={emp?.posting} /></div>
            <div className="col-md-6"><FieldLabel label="Intimation Date" /><ReadOnlyField value={resig.intimation_date ? formatDate(resig.intimation_date) : ""} /></div>
            <div className="col-md-6"><FieldLabel label="Notice End Date" /><ReadOnlyField value={resig.notice_end_date ? formatDate(resig.notice_end_date) : ""} /></div>
            <div className="col-md-6"><FieldLabel label="Stand Relieved Date" /><ReadOnlyField value={resig.relieved_wef ? formatDate(resig.relieved_wef) : ""} /></div>
          </div>
        </SectionCard>

        {/* Section 2 — Service Bond Details */}
        <SectionCard index={1} title="Service Bond Details">
          <InfoRow label="Bond Period" value={bond?.bond_period} />
          <InfoRow label="Bond Amount" value={formatINR(bondAmount)} valueColor="#C0282C" bold />
          <InfoRow label="Training Expenditure" value={formatINR(Number(bond?.training_expenditure) || 0)} />
          <InfoRow label="Service Completed under Bond" value={bond?.service_completed} />
          <InfoRow label="Bond Start Date" value={bond?.bond_start ? formatDate(bond.bond_start) : "—"} />
          <InfoRow label="Bond End Date" value={bond?.bond_end ? formatDate(bond.bond_end) : "—"} />
        </SectionCard>

        {/* Section 3 — Waiver Request */}
        <SectionCard index={2} title="Waiver Request">
          <div className="row g-3">
            <div className="col-12">
              <FieldLabel label="Date of Waiver Request" />
              <ReadOnlyField value={formatDate(today)} />
              <small className="text-muted d-block mt-1">Auto-set to today's date</small>
            </div>
            <div className="col-12">
              <FieldLabel label="Waiver Type" required />
              <RadioGroup
                value={waiverType}
                onChange={setWaiverType}
                name="waiverType"
                options={[
                  { label: "Full Waiver", value: "full" },
                  { label: "Partial Waiver", value: "partial" },
                ]}
              />
            </div>
            {waiverType === "partial" && (
              <div className="col-12">
                <FieldLabel label="Requested Waiver Amount (₹)" required />
                <input
                  type="number"
                  className="form-control"
                  placeholder="Enter the amount you wish to be waived"
                  value={requestedAmount}
                  onChange={(e) => setRequestedAmount(e.target.value)}
                />
                <small className="text-muted d-block mt-1">
                  Maximum waivable: {formatINR(bondAmount)}
                </small>
              </div>
            )}
            <div className="col-12">
              <FieldLabel label="Reason for Waiver" required />
              <textarea
                className="form-control"
                rows={5}
                placeholder="Provide detailed justification for requesting bond waiver..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <small className="text-muted d-block mt-1">
                Provide a comprehensive justification. MD will review this carefully.
              </small>
            </div>
          </div>
        </SectionCard>

        {/* Section 4 — Approval Workflow */}
        <SectionCard index={3} title="Approval Workflow">
          <p className="text-muted small mb-2">
            Bond waiver requests require MD approval. The request will be routed through:
          </p>
          <ApprovalTimeline
            steps={[
              { level: "Controlling Officer / Dy. HOD", status: "Pending" },
              { level: "HOD — Recommendation", status: "Pending" },
              { level: "HR — Financial Evaluation", status: "Pending" },
              { level: "Managing Director (MD) — Final Authority", status: "Pending" },
            ]}
          />
        </SectionCard>

        {/* HR & Finance Impact */}
        <SectionCard index={4} title="HR & Finance Impact Assessment">
          <p className="text-muted small mb-2">
            Upon submission, HR will evaluate the following before forwarding to MD:
          </p>
          <ul className="small mb-0">
            <li>Bond period completion status</li>
            <li>Bond conditions and clauses</li>
            <li>Training expenditure incurred by DMRC</li>
            <li>Incentives / benefits given during bond period</li>
            <li>Pending recoveries from employee</li>
            <li>Cost impact estimate (by Finance)</li>
          </ul>
        </SectionCard>

        {/* Outcomes */}
        <SectionCard index={5} title="Possible Outcomes">
          <div className="d-flex flex-column gap-3">
            <div className="d-flex align-items-start gap-2">
              <i className="ti ti-circle-check mt-1" style={{ color: "#049123", fontSize: 18 }} />
              <div>
                <div className="fw-semibold small" style={{ color: "#049123" }}>Full Waiver Approved</div>
                <div className="small text-muted">Bond recovery amount set to ₹0. No deductions in final settlement.</div>
              </div>
            </div>
            <div className="d-flex align-items-start gap-2">
              <i className="ti ti-circle-minus mt-1" style={{ color: "#BA7517", fontSize: 18 }} />
              <div>
                <div className="fw-semibold small" style={{ color: "#BA7517" }}>Partial Waiver Approved</div>
                <div className="small text-muted">Reduced recovery amount as decided by MD. Remaining deducted from settlement.</div>
              </div>
            </div>
            <div className="d-flex align-items-start gap-2">
              <i className="ti ti-circle-x mt-1" style={{ color: "#FF0F0F", fontSize: 18 }} />
              <div>
                <div className="fw-semibold small" style={{ color: "#FF0F0F" }}>Waiver Rejected</div>
                <div className="small text-muted">Full bond recovery as per original bond terms.</div>
              </div>
            </div>
          </div>
        </SectionCard>
      </div>

      <BottomActionBar
        primaryLabel="Submit Waiver Request"
        primaryIcon="ti-send"
        secondaryLabel="Cancel"
        onPrimary={handleSubmit}
        onSecondary={handleCancel}
        primaryColor="#4A1A6E"
        disabled={noResignation || noBond || !reason.trim() || submitting}
      />
    </div>
  );
};

export default BondWaiver;
